import { useTranslation } from 'react-i18next'
import { ErrorCircleIcon, InfoCircleIcon, Span, Text } from '@/components/atoms'
import { CalendarPanel } from '../calendar-panel'
import { formatDate } from '../calendar-panel/use-calendar'
import { useDateRangePickerField } from './date-range-picker-field.hook'

export interface DateRange {
  start: Date | undefined
  end: Date | undefined
}

interface DateRangePickerFieldProps {
  label?: string
  value: DateRange
  fromLabel?: string
  toLabel?: string
  helpText?: string
  error?: boolean | string
  tooltip?: string
  optional?: boolean
  disabled?: boolean
  withActions?: boolean
  minDate?: Date
  maxDate?: Date
  onChange: (range: DateRange) => void
  onBlur?: () => void
}

export function DateRangePickerField({
  label,
  value,
  fromLabel,
  toLabel,
  helpText,
  error,
  tooltip,
  optional,
  disabled = false,
  withActions = false,
  minDate,
  maxDate,
  onChange,
  onBlur,
}: DateRangePickerFieldProps) {
  const { t } = useTranslation()
  const {
    ref,
    isOpen,
    hoverDate,
    setHoverDate,
    activeRange,
    hasError,
    displayedHelp,
    fromPlaceholder,
    toPlaceholder,
    iconClassname,
    viewDate,
    prevMonth,
    nextMonth,
    days,
    openFrom,
    openTo,
    close,
    handleConfirm,
    handleDayClick,
    triggerClasses,
  } = useDateRangePickerField({
    value,
    disabled,
    withActions,
    error,
    helpText,
    fromLabel,
    toLabel,
    onChange,
    onBlur,
  })

  return (
    <div className="flex w-full flex-col gap-3">
      <div ref={ref} className="relative flex flex-col gap-2">
        <div className="flex items-end gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            {label && (
              <div className="flex items-center gap-1">
                <Text as="label" className="text-neutral-900">
                  {label}
                  {optional && (
                    <Text as="span" className="text-neutral-300">
                      {` (${t('common.optional')})`}
                    </Text>
                  )}
                </Text>
                {tooltip && (
                  <div className="tooltip" data-tip={tooltip}>
                    <InfoCircleIcon className="text-indigo-base h-4 w-4" />
                  </div>
                )}
              </div>
            )}
            <div className="relative">
              <button
                type="button"
                disabled={disabled}
                className={triggerClasses(isOpen, !!value.start)}
                onClick={openFrom}
              >
                {value.start ? formatDate(value.start) : fromPlaceholder}
              </button>
              {hasError ? <ErrorCircleIcon className={iconClassname} /> : <></>}
            </div>
          </div>

          <div className="relative min-w-0 flex-1">
            <button
              type="button"
              disabled={disabled}
              className={triggerClasses(isOpen, !!value.end)}
              onClick={openTo}
            >
              {value.end ? formatDate(value.end) : toPlaceholder}
            </button>
            {hasError ? <ErrorCircleIcon className={iconClassname} /> : <></>}
          </div>
        </div>

        {isOpen && (
          <div className="absolute top-full right-0 left-0 z-(--z-dropdown) mt-1 rounded-2xl shadow-md">
            <CalendarPanel
              viewDate={viewDate}
              days={days}
              onPrevMonth={prevMonth}
              onNextMonth={nextMonth}
              startDate={activeRange.start}
              endDate={activeRange.end}
              hoverDate={hoverDate}
              minDate={minDate}
              maxDate={maxDate}
              onDayClick={handleDayClick}
              onDayHover={setHoverDate}
              onConfirm={withActions ? handleConfirm : undefined}
              onCancel={withActions ? close : undefined}
            />
          </div>
        )}
      </div>

      {displayedHelp && (
        <Span className={hasError ? 'text-error-base' : 'text-neutral-900'}>
          {displayedHelp}
        </Span>
      )}
    </div>
  )
}
